import * as XLSX from "xlsx";
import { useState } from "react";
import { TextField, Button, Typography, Grid, Box, Paper } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UploadIcon from "@mui/icons-material/Upload";
import { fieldConfig, getInitialState, labelToNameMap } from "./DefaultParameters";

/**
 * Parameters form for MicroSlide. Mirrors MicroTip/Parameters.js.
 *
 * Supports:
 *  - drag & drop / upload of TXT / CSV / XLS / XLSX
 *  - inline paste (TXBParse) with smart parser (= : tab ,)
 *  - reset to defaults
 *  - one TextField per assay parameter
 */
export default function Parameters({ parameters, setParameters }) {
    const [dragActive, setDragActive] = useState(false);

    const parseSmart = (text) => {
        const lines = text.split("\n");
        const updated = {};

        lines.forEach((line) => {
            if (!line.trim()) return;
            let parts;
            if (line.includes("=")) parts = line.split("=");
            else if (line.includes(":")) parts = line.split(":");
            else if (line.includes("\t")) parts = line.split("\t");
            else if (line.includes(",")) parts = line.split(",");
            else return;
            if (parts.length < 2) return;

            const rawKey = parts[0].trim();
            const value = parts.slice(1).join("").trim();
            const mappedName = labelToNameMap[rawKey.toLowerCase()] || rawKey;
            updated[mappedName] = value;
        });
        return updated;
    };

    const parseByOrder = (text) => {
        const lines = text.split("\n");
        const updated = {};
        fieldConfig.forEach((field, index) => {
            if (lines[index]?.trim()) updated[field.name] = lines[index].trim();
        });
        return updated;
    };

    const handleParse = () => {
        const txt = parameters.TXBParse || "";
        const parsed =
            txt.includes("=") || txt.includes(":") || txt.includes("\t") || txt.includes(",")
                ? parseSmart(txt)
                : parseByOrder(txt);
        setParameters(prev => ({ ...prev, ...parsed }));
    };

    const handleReset = () => setParameters(getInitialState());

    const handleChange = (e) => {
        const { name, value } = e.target;
        setParameters(prev => ({ ...prev, [name]: value }));
    };

    const processFile = (file) => {
        if (!file) return;
        const name = file.name.toLowerCase();

        if (name.endsWith(".txt") || name.endsWith(".csv")) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const text = evt.target.result;
                setParameters(prev => ({ ...prev, TXBParse: text }));
                const parsed = parseSmart(text);
                setParameters(prev => ({ ...prev, ...parsed }));
            };
            reader.readAsText(file);
        } else if (name.endsWith(".xls") || name.endsWith(".xlsx")) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                const parsed = {};
                json.forEach((row) => {
                    if (row[0] !== undefined && row[1] !== undefined) {
                        const key = row[0].toString().trim().toLowerCase();
                        const mappedName = labelToNameMap[key] || row[0];
                        parsed[mappedName] = row[1].toString();
                    }
                });
                setParameters(prev => ({ ...prev, ...parsed }));
            };
            reader.readAsArrayBuffer(file);
        } else {
            alert("Unsupported file type. Please use TXT, CSV or XLSX.");
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) processFile(file);
    };

    return (
        <Paper elevation={0} sx={{ margin: "auto", border: "none" }}>
            <Box>
                <Box
                    position="relative"
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={(e) => { e.preventDefault(); setDragActive(false); handleDrop(e); }}
                    sx={{
                        border: dragActive ? "2px dashed #1976d2" : "0px",
                        borderRadius: 1,
                        p: 1,
                        backgroundColor: dragActive ? "rgba(25,118,210,0.05)" : "transparent"
                    }}
                >
                    <TextField
                        name="TXBParse"
                        value={parameters.TXBParse || ""}
                        onChange={handleChange}
                        multiline
                        rows={5}
                        fullWidth
                    />

                    {!parameters.TXBParse && (
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            height="100%"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ pointerEvents: "none", textAlign: "center" }}
                        >
                            <Typography variant="subtitle1">
                                Drag &amp; drop, paste or enter assay parameters.
                            </Typography>
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<UploadFileIcon />}
                                sx={{ mt: 1, pointerEvents: "auto" }}
                            >
                                Upload File
                                <input
                                    type="file"
                                    hidden
                                    accept=".txt,.csv,.xls,.xlsx"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) processFile(file);
                                    }}
                                />
                            </Button>
                        </Box>
                    )}
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} mt={2}>
                    <Box display="flex" gap={1}>
                        <Button variant="contained" startIcon={<UploadIcon />} onClick={handleParse}>
                            Update
                        </Button>
                        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}>
                            Reset
                        </Button>
                    </Box>
                </Box>

                <Grid container spacing={2}>
                    {fieldConfig.map(field => (
                        <Grid item xs={12} sm={6} md={4} key={field.name}>
                            <TextField
                                label={field.label}
                                name={field.name}
                                value={parameters[field.name] ?? ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Paper>
    );
}
